import { motion } from "framer-motion";
import {
  Users, Briefcase, MessageSquare, CheckCircle, FileText, Loader2
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subMonths, startOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import PeriodFilter, { type PeriodMonths } from "@/components/PeriodFilter";
import PeriodComparisonChart, { type ComparisonDataPoint } from "@/components/PeriodComparisonChart";

function buildMonthlyData(rows: { created_at: string }[], months: Date[]) {
  const counts = new Map<string, number>();
  months.forEach((m) => counts.set(format(m, "yyyy-MM"), 0));
  rows.forEach((r) => {
    const key = r.created_at.slice(0, 7);
    if (counts.has(key)) counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return months.map((m) => ({
    month: format(m, "MMM", { locale: ptBR }),
    value: counts.get(format(m, "yyyy-MM")) ?? 0,
  }));
}

export default function EmpresaRelatorios() {
  const { orgId, isLoading: ctxLoading } = usePartnerContext();
  const [period, setPeriod] = useState<PeriodMonths>(6);
  const now = new Date();
  const months = Array.from({ length: period }, (_, i) => startOfMonth(subMonths(now, period - 1 - i)));
  const sinceISO = months[0].toISOString();

  const { data: stats, isLoading } = useQuery({
    queryKey: ["empresa_relatorio_stats", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const [shortlists, contacts, applications, jobs] = await Promise.all([
        supabase.from("shortlists").select("id", { count: "exact", head: true }).eq("org_id", orgId!),
        supabase.from("contact_requests").select("id, status", { count: "exact", head: false }).eq("org_id", orgId!),
        supabase.from("job_applications").select("id, job_id").in(
          "job_id",
          (await supabase.from("job_posts").select("id").eq("org_id", orgId!)).data?.map(j => j.id) ?? []
        ),
        supabase.from("job_posts").select("id, status").eq("org_id", orgId!),
      ]);

      const contactData = contacts.data ?? [];

      return {
        shortlistsCriadas: shortlists.count ?? 0,
        pedidosContato: contactData.length,
        contatosAprovados: contactData.filter(c => c.status === "approved").length,
        candidaturas: applications.data?.length ?? 0,
        vagasPublicadas: (jobs.data ?? []).filter(j => j.status === "publicada").length,
      };
    },
  });

  const prevMonths = Array.from({ length: period }, (_, i) => startOfMonth(subMonths(now, 2 * period - 1 - i)));
  const prevSinceISO = prevMonths[0].toISOString();

  const { data: chartData, isLoading: chartsLoading } = useQuery({
    queryKey: ["empresa_relatorio_charts", orgId, sinceISO, period],
    enabled: !!orgId,
    queryFn: async () => {
      const jobIds = (await supabase.from("job_posts").select("id").eq("org_id", orgId!)).data?.map(j => j.id) ?? [];

      const [applications, contacts] = await Promise.all([
        jobIds.length > 0
          ? supabase.from("job_applications").select("created_at").in("job_id", jobIds).gte("created_at", sinceISO)
          : Promise.resolve({ data: [] }),
        supabase.from("contact_requests").select("created_at").eq("org_id", orgId!).gte("created_at", sinceISO),
      ]);

      return {
        applications: buildMonthlyData(applications.data ?? [], months),
        contacts: buildMonthlyData(contacts.data ?? [], months),
      };
    },
  });

  const { data: comparisonData, isLoading: compLoading } = useQuery({
    queryKey: ["empresa_relatorio_comparison", orgId, sinceISO, prevSinceISO, period],
    enabled: !!orgId,
    queryFn: async () => {
      const jobIds = (await supabase.from("job_posts").select("id").eq("org_id", orgId!)).data?.map(j => j.id) ?? [];

      const [curApps, prevApps, curContacts, prevContacts] = await Promise.all([
        jobIds.length > 0
          ? supabase.from("job_applications").select("created_at").in("job_id", jobIds).gte("created_at", sinceISO)
          : Promise.resolve({ data: [] }),
        jobIds.length > 0
          ? supabase.from("job_applications").select("created_at").in("job_id", jobIds).gte("created_at", prevSinceISO).lt("created_at", sinceISO)
          : Promise.resolve({ data: [] }),
        supabase.from("contact_requests").select("created_at").eq("org_id", orgId!).gte("created_at", sinceISO),
        supabase.from("contact_requests").select("created_at").eq("org_id", orgId!).gte("created_at", prevSinceISO).lt("created_at", sinceISO),
      ]);

      return [
        { label: "Candidaturas", current: curApps.data?.length ?? 0, previous: prevApps.data?.length ?? 0 },
        { label: "Contatos", current: curContacts.data?.length ?? 0, previous: prevContacts.data?.length ?? 0 },
      ] as ComparisonDataPoint[];
    },
  });

  if (ctxLoading || isLoading) {
    return (
      <div className="page-container-desktop flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const kpis = [
    { icon: Users, label: "Candidaturas recebidas", value: stats?.candidaturas ?? 0, color: "bg-primary/10 text-primary" },
    { icon: FileText, label: "Shortlists criadas", value: stats?.shortlistsCriadas ?? 0, color: "bg-accent/20 text-accent" },
    { icon: MessageSquare, label: "Pedidos de contato", value: stats?.pedidosContato ?? 0, color: "bg-cyan-100 text-cyan-600" },
    { icon: Briefcase, label: "Vagas publicadas", value: stats?.vagasPublicadas ?? 0, color: "bg-violet-100 text-violet-600" },
    { icon: CheckCircle, label: "Contatos aprovados", value: stats?.contatosAprovados ?? 0, color: "bg-success/10 text-success" },
  ];

  const charts = [
    { title: "Candidaturas a Vagas", data: chartData?.applications ?? [], color: "hsl(var(--primary))", fillId: "fillAppsEmp" },
    { title: "Pedidos de Contato", data: chartData?.contacts ?? [], color: "hsl(168, 60%, 45%)", fillId: "fillContactsEmp" },
  ];

  return (
    <div className="page-container-desktop">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Relatórios</h1>
          <p className="text-muted-foreground mt-1">Métricas agregadas de impacto</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
      </motion.div>

      {/* KPIs */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {kpis.map((kpi, i) => (
          <div key={i} className="bg-card rounded-2xl p-5 border border-border">
            <div className={`w-10 h-10 rounded-xl ${kpi.color} flex items-center justify-center mb-3`}>
              <kpi.icon className="w-5 h-5" />
            </div>
            <p className="text-3xl font-bold text-foreground">{kpi.value}</p>
            <p className="text-sm text-muted-foreground">{kpi.label}</p>
          </div>
        ))}
      </motion.div>

      {/* Monthly Evolution Charts */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {charts.map((chart, i) => (
          <div key={i} className="bg-card rounded-2xl p-5 border border-border">
            <h3 className="font-heading text-sm font-semibold text-foreground mb-4">{chart.title}</h3>
            <div className="h-44">
              {chartsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chart.data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id={chart.fillId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={chart.color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={chart.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                      labelStyle={{ color: "hsl(var(--foreground))" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={chart.color}
                      strokeWidth={2}
                      fill={`url(#${chart.fillId})`}
                      name="Total"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Period Comparison */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
        className="bg-card rounded-2xl p-5 border border-border mb-8">
        <h3 className="font-heading font-semibold text-foreground mb-4">Comparativo entre Períodos</h3>
        <div className="h-52">
          <PeriodComparisonChart
            data={comparisonData ?? []}
            isLoading={compLoading}
            periodLabel={`Últimos ${period} meses`}
            previousLabel={`${period} meses anteriores`}
          />
        </div>
      </motion.div>

      {/* LGPD Notice */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
        className="bg-primary/5 rounded-xl p-4 border border-primary/10">
        <p className="text-sm text-muted-foreground text-center">
          📊 Dados agregados — sem identificação pessoal. Conforme LGPD.
        </p>
      </motion.div>
    </div>
  );
}
